import type {
  FavoriteListingsOfContact,
  ProcessedListingData
} from "../../@types/sharedSchema";

/**
 * Helpers for rendering the AI watermark-free versions of listing photos
 * (`ProcessedListingData.watermarkFreeImages`).
 *
 * The clean URLs live on the SHARED listing document — a cross-user cache, so a second realtor's
 * removal costs nothing. As of 2026-09-30 DISPLAY is shared too: there is exactly ONE gate, the
 * realtor's rights acceptance (`User.watermarkTermsAcceptedAt`), which is per REALTOR and granted
 * once. Accept it, and every listing that has clean photos renders clean on all of that realtor's
 * minisites, forever, including links already sent, at no cost.
 *
 * There is no per-(user, listing) gate and no show/hide toggle. Both existed and both were removed
 * — see the note on `userHasRemovedListingWatermarks` below for why the per-listing one protected
 * nothing while leaking customer emails. Photos without a clean version always fall back to the
 * original.
 */

/** original (watermarked) URL → clean URL for one listing. */
export function buildWatermarkFreeUrlMap(
  listing: Pick<ProcessedListingData, "watermarkFreeImages">
): Map<string, string> {
  return new Map(
    (listing.watermarkFreeImages ?? []).map((image) => [image.sourceImageUrl, image.cleanImageUrl])
  );
}

/**
 * ⚠️ `userHasRemovedListingWatermarks` was DELETED on 2026-09-30, and must not come back.
 *
 * It tested `watermarkRemovalRequestedBy` (plus `createdBy` authorship) to decide whether THIS user
 * saw the clean versions. It protected nothing — the clean images are uploaded with
 * `predefinedAcl: 'publicRead'` and a token-less URL, pointed to from `yad2-listings-latest`
 * (`allow get: if true`), and `favorite-listings-of-contact` (`allow read: if true`) hands any
 * anonymous buyer the listing ids. Meanwhile the array itself was a world-readable roster of our
 * customers' email addresses, and the gate made a fully-clean listing report
 * "0 מתוך 12 תמונות נקיות" to anyone who had not personally run it.
 *
 * The one remaining gate is the rights assertion, `User.watermarkTermsAcceptedAt` — per REALTOR,
 * accepted once, forever. On the public minisite it arrives as
 * `PublicRealtorInfoResponse.watermarkTermsAccepted`, in the same payload the page already blocks
 * on, which is what lets `resolveListingPictures` decide everything from one snapshot.
 */

/**
 * Best display cover for a listing card. `coverImageUrl` is stored at its OWN storage
 * path (`.../cover.jpg`) and is never a member of `imageUrls` — watermark removal only
 * processes `imageUrls` entries, so the cover URL itself never gains a clean version.
 * Since the cover is (visually) the first gallery photo, fall back to the CLEAN first
 * gallery image when the caller's map covers it — that is the same photo, without the
 * watermark. Pass a listing whose watermarkFreeImages was already gated per-user
 * (empty/absent for a non-remover), and this degrades to the original cover.
 */
export function pickListingDisplayCoverUrl(
  listing: Pick<ProcessedListingData, "coverImageUrl" | "imageUrls" | "watermarkFreeImages">
): string | undefined {
  const urlMap = buildWatermarkFreeUrlMap(listing);
  const cover = listing.coverImageUrl;
  const firstImage = listing.imageUrls?.[0];
  if (cover && urlMap.has(cover)) return urlMap.get(cover);
  if (firstImage && urlMap.has(firstImage)) return urlMap.get(firstImage);
  return cover || firstImage;
}

/**
 * Returns a copy of the listing with `imageUrls` / `coverImageUrl` swapped to their
 * watermark-free versions where one exists. Returns the input untouched when the
 * listing has no clean images at all.
 */
export function applyWatermarkFreeImagesToListing<
  T extends Pick<ProcessedListingData, "imageUrls" | "coverImageUrl" | "watermarkFreeImages">
>(listing: T): T {
  const urlMap = buildWatermarkFreeUrlMap(listing);
  if (!urlMap.size) return listing;

  return {
    ...listing,
    imageUrls: listing.imageUrls?.map((url) => urlMap.get(url) ?? url),
    coverImageUrl: listing.coverImageUrl
      ? urlMap.get(listing.coverImageUrl) ?? listing.coverImageUrl
      : listing.coverImageUrl
  };
}

/**
 * Hidden pictures are stored on the minisite doc as ORIGINAL image URLs. When a surface renders the
 * watermark-free versions instead, the hidden-URL lists must be translated through the SAME
 * original→clean mapping, or the hidden photos stop matching and reappear.
 * Returns a display-only copy of the doc — never write it back to Firestore.
 *
 * ⚠️ THIS AND THE IMAGE SWAP ARE ONE DECISION. Whatever decides to swap a listing's `imageUrls`
 * must pass that same listing here, from the same input, in the same pass. They used to be two
 * `useMemo`s on the public minisite, each independently evaluating a PER-LISTING predicate
 * ("did this realtor run the removal?") over a different array — and any ordering where the swap
 * landed but the translation did not renders a photo the realtor deliberately hid. Nothing forced
 * them to agree.
 *
 * What makes it safe now is that the gate is a single page-level boolean — the realtor's rights
 * acceptance — rather than a per-listing test, so both derivations are driven by one input and
 * cannot diverge. Keep it that way: if a per-listing condition ever comes back, these two must be
 * computed together, in one function, returning both.
 */
export function applyWatermarkFreeImagesToFavoriteDoc(
  favoriteDoc: FavoriteListingsOfContact,
  listings: Array<Pick<ProcessedListingData, "id" | "watermarkFreeImages">>
): FavoriteListingsOfContact {
  const additionalDetails = favoriteDoc.additionalDetails;
  if (!additionalDetails) return favoriteDoc;

  const urlMapByListingId = new Map(
    listings.map((listing) => [listing.id, buildWatermarkFreeUrlMap(listing)])
  );

  const translatedDetails: FavoriteListingsOfContact["additionalDetails"] = {};
  for (const [listingId, details] of Object.entries(additionalDetails)) {
    const urlMap = urlMapByListingId.get(listingId);
    const hiddenPictures = details?.hiddenPictures;
    if (!urlMap?.size || !hiddenPictures?.pictures?.length) {
      translatedDetails[listingId] = details;
      continue;
    }
    translatedDetails[listingId] = {
      ...details,
      hiddenPictures: {
        ...hiddenPictures,
        pictures: hiddenPictures.pictures.map((url) => urlMap.get(url) ?? url)
      }
    };
  }

  return { ...favoriteDoc, additionalDetails: translatedDetails };
}
